/// <amd-module name="Wit/WorkItemFormMarkdown/WorkItemFormMarkdownEditor" />
import { IRenderedEditor } from "Comments/Editor/Components/CommentEditor.Props";
import { MarkdownTextFieldWithAutocomplete } from "Mention/MarkdownWithAutocomplete/Components";
import { format } from "VSS/Core/Util/String";
import { shortcutCommands } from "VSS/Features/MarkdownEditor/Commands/Shortcut";
import { IMarkdownTextFieldProps } from "VSS/Features/MarkdownEditor/Components";
import { insertText } from "VSS/Features/MarkdownEditor/Formatting/Common";
import { IMarkdownFormattingArgs, IMarkdownTextSource } from "VSS/Features/MarkdownEditor/Formatting/Contracts";
import { IVssPageContext } from "VSS/Platform/Context";
import { usePageContext } from "VSS/Platform/Layout";
import * as Resources from "Wit/WorkItemFormMarkdown/Resources";
import * as React from "react";
import * as TurndownService from "turndown";
import { validFileTypeList } from "./FileUploadUtils";
import { nestedLists } from "./NestedListsPlugin";
import { tables } from "./TablesPlugin";
import { maxFileSize } from "./WorkItemFormMarkdownToolbar";

export class WorkItemFormMarkdownEditor extends MarkdownTextFieldWithAutocomplete implements IRenderedEditor {
    public constructor(props: IMarkdownTextFieldProps) {
        super(props);
    }

    public getValue(): string {
        return this.getInputElement()?.value ?? "";
    }

    public isEmpty(): boolean {
        return this.getValue() === "";
    }

    public setContent(newContent: string): void {
        const inputElement = this.getInputElement();
        if (inputElement) {
            inputElement.value = newContent;
        }
    }

    public selectAll(): void {
        const inputElement = this.getInputElement();
        if (inputElement) {
            inputElement.select();
        }
    }

    public focus(): void {
        const inputElement = this.getInputElement();
        if (inputElement) {
            inputElement.focus();
        }
    }

    public setEnabled(enabled: boolean): void {}
}

export type WorkItemFormMarkdownEditorMemoizedProps = IMarkdownTextFieldProps & {
    uploadImage: (file: File) => Promise<string>;
};

export const WorkItemFormMarkdownEditorMemoized = React.forwardRef<WorkItemFormMarkdownEditor, WorkItemFormMarkdownEditorMemoizedProps>(
    (props, ref) => {
        const pageContext = usePageContext();
        return React.useMemo(
            () => (
                <WorkItemFormMarkdownEditor
                    ref={ref}
                    shortcutCommands={shortcutCommands}
                    onPaste={(e, textSource) => handlePasteEvent(pageContext, e, textSource, props.uploadImage)}
                    messageText={Resources.MarkdownEditorSuggestionMessage}
                    {...props}
                />
            ),
            [props.error, props.autoAdjustHeight]
        );
    }
);

const htmlDataType = "text/html";

const handlePasteEvent = async (
    pageContext: IVssPageContext,
    event: React.ClipboardEvent<HTMLTextAreaElement>,
    textSource: IMarkdownTextSource | null,
    uploadImage: (file: File) => Promise<string>
): Promise<void> => {
    if (!textSource) {
        return;
    }
    let formattingArgs = textSource.getFormattingArgs();
    if (!(formattingArgs && event.clipboardData)) {
        return;
    }
    if (
        event.clipboardData.types.includes(htmlDataType) &&
        !event.isDefaultPrevented() // happens when the paste event is handled by AutocompleteComponent
    ) {
        try {
            const htmlData = event.clipboardData.getData(htmlDataType);
            const turndownService = new TurndownService();
            turndownService.use(tables);
            turndownService.use(nestedLists);
            const htmlDataWithoutComments = htmlData.replace(/<\!--(?!>)[\S\s]*?-->/g, "");
            const markdownData = turndownService.turndown(htmlDataWithoutComments);
            const newData = markdownData;
            textSource.applyFormatting(insertText(formattingArgs, newData));
            event.preventDefault();
            return;
        } catch (e) {
            // We do not need to inform the user about this error as the paste event will continue and result in pasting text data
            console.error("Error while converting pasted data from HTML to Makrdown", e);
        }
    }

    const { files, items } = event.clipboardData;
    if (files && files.length > 0) {
        await addFileToMarkdown(files[0], formattingArgs, textSource, uploadImage);
    } else if (items && items.length > 0) {
        const itemAsFile = items[0].getAsFile();
        if (itemAsFile) {
            await addFileToMarkdown(itemAsFile, formattingArgs, textSource, uploadImage);
        }
    }
};

const getMB = (bytes: number) => Math.round((bytes / 1024 / 1024) * 10) / 10;
let uploadedFilesCount = 0;
const addFileToMarkdown = async (
    file: File,
    formattingArgs: IMarkdownFormattingArgs,
    textSource: IMarkdownTextSource,
    uploadImage: (file: File) => Promise<string>
): Promise<void> => {
    const fileExtension = file.name.split(".").pop();
    if (file.type.indexOf("image/") >= 0 && validFileTypeList.some(validExtension => validExtension === fileExtension)) {
        if (file.size > maxFileSize) {
            textSource.showError(new Error(format(Resources.MarkdownEditorImageSizeTooBig, getMB(file.size), getMB(maxFileSize))));
        } else {
            const uploadingText = format(Resources.MarkdownEditorImageUploading, `${file.name}${++uploadedFilesCount}`);
            const getVariablesForFromatting = () => {
                const currentFormattingArgs = textSource.getFormattingArgs()!;
                const loadingStartPosition = currentFormattingArgs.text.indexOf(uploadingText);
                const loadingEndPosition = loadingStartPosition + uploadingText.length;
                return { currentFormattingArgs, loadingStartPosition, loadingEndPosition };
            };
            try {
                textSource.applyFormatting(insertText(formattingArgs, uploadingText));
                const uploadedImageUrl = await uploadImage(file);
                const { currentFormattingArgs, loadingStartPosition, loadingEndPosition } = getVariablesForFromatting();
                textSource.applyFormatting(
                    insertText(
                        { selectionStart: loadingStartPosition, selectionEnd: loadingEndPosition, text: currentFormattingArgs.text },
                        `![${file.name}](${uploadedImageUrl}) `
                    )
                );
            } catch (e) {
                textSource.showError(new Error(format(Resources.MarkdownEditorUploadFileError, e.message)));
                const { currentFormattingArgs, loadingStartPosition, loadingEndPosition } = getVariablesForFromatting();
                textSource.applyFormatting(
                    insertText({ selectionStart: loadingStartPosition, selectionEnd: loadingEndPosition, text: currentFormattingArgs.text }, " ")
                );
            }
        }
    } else {
        textSource.showError(new Error(format(Resources.MarkdownEditorImageExtensionNotSupported, fileExtension, validFileTypeList.join(", "))));
    }
};
